import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Heading, Text, SimpleGrid, Flex, Icon, Image, Badge, Spinner, useToast,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaCalendarTimes } from 'react-icons/fa';
import PageHero from '../Components/PageHero';
import { fetchPromotions, formatDate, localizeRow, resolveImageUrl } from '../lib/content';
import promoImage1 from '../Components/Assetes/home2.jpg';
import promoImage2 from '../Components/Assetes/home3.jpg';
import promoImage3 from '../Components/Assetes/1113.jpeg';

const promoImages = {
  'home2.jpg': promoImage1,
  'home3.jpg': promoImage2,
  '1113.jpeg': promoImage3,
};

const PromotionsSection = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchPromotions().then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error('PROMOTIONS ERROR', error);
        toast({
          title: `${t('promotions.errorPrefix')}: ${error.message}`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }
      setPromotions(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [toast, t]);

  return (
    <Box pb={16}>
      <PageHero title={t('promotions.heroTitle')} subtitle={t('promotions.subtitle')} />
      <Box as="section" py={{ base: 14, md: 20 }} bg="surface.muted">
        <Container maxW="7xl">
          <Box maxW="700px" mx="auto" textAlign="center" mb={12}>
            <Heading as="h2" size="xl">
              {t('promotions.title')}
            </Heading>
            <Box w="56px" h="5px" bg="brand.500" borderRadius="full" mx="auto" mt={4} />
          </Box>
          {loading ? (
            <Flex justify="center" py={16}>
              <Spinner size="xl" color="brand.500" thickness="4px" />
            </Flex>
          ) : promotions.length === 0 ? (
            <Box
              bg="surface.base"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="border.subtle"
              p={{ base: 12, md: 16 }}
              textAlign="center"
            >
              <Text color="text.soft" fontSize="lg">
                {t('promotions.comingSoon')}
              </Text>
            </Box>
          ) : (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {promotions.map((promotion) => {
              const imageSrc = resolveImageUrl(promotion.image, promoImages);
              const item = localizeRow(promotion, i18n.language);
              return (
                <Box
                  key={promotion.id}
                  bg="surface.base"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="border.subtle"
                  boxShadow="sm"
                  overflow="hidden"
                  transition="transform 0.25s ease, boxShadow 0.25s ease"
                  _hover={{ transform: 'translateY(-6px)', boxShadow: 'xl' }}
                >
                  {imageSrc ? (
                    <Image
                      src={imageSrc}
                      alt={item.title}
                      objectFit="cover"
                      w="100%"
                      h="200px"
                    />
                  ) : (
                    <Box h="200px" bgGradient="linear(to-br, brand.500, brand.700)" />
                  )}
                  <Box textAlign="center" p={8}>
                    <Heading as="h3" size="md" mb={3}>
                      {item.title}
                    </Heading>
                    <Text color="text.muted" fontSize="sm" mb={6}>
                      {item.description}
                    </Text>
                  <Flex justify="center" gap={3} flexWrap="wrap">
                    {promotion.start_date && (
                      <Badge colorScheme="brand" borderRadius="full" px={4} py={1.5} fontSize="sm">
                        <Icon as={FaCalendarAlt} mr={2} />
                        {t('promotions.startDate')} {formatDate(promotion.start_date, i18n.language)}
                      </Badge>
                    )}
                    {promotion.end_date && (
                      <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={4} py={1.5} fontSize="sm">
                        <Icon as={FaCalendarTimes} mr={2} />
                        {t('promotions.dueDate')} {formatDate(promotion.end_date, i18n.language)}
                      </Badge>
                    )}
                  </Flex>
                </Box>
              </Box>
              );
            })}
          </SimpleGrid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default PromotionsSection;
